import { OfferPointsComponent } from '../../../../components/offer-points/offer-points.component';
import {
  Component,
  Input,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  model,
  OnInit,
  Output,
  signal,
  viewChild,
  viewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule, NgModel } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { LicenseApiService } from 'src/app/services/license-api.service';
import { BillingOrganisation } from 'src/app/services/billing-profile-api.service';
import { ProfileService } from 'src/app/services/profile.service';
import { LocationsService } from 'src/app/services/locations.service';
import {
  clearNegotiatedOfferSelection,
  loadMunicipalityPlanSelection,
  loadNegotiatedOfferSelection,
  type MunicipalityPlanSelection,
} from 'src/app/pages/onboarding/municipality-plan-selection';
import {
  UserBillDetail,
  PromotionCodeInfo,
  NegotiatedOffer,
} from 'src/app/types/rest-api';
import {
  calcPromoDiscount,
  promoDiscountLabel,
} from 'src/app/pages/onboarding/promotion-code-discount';
import { formatChf, formatRappenChf } from 'src/app/utils/currency-formatter';
import { GeoLocation } from 'src/app/types/core';
import { AddressAutocompleteFieldComponent } from 'src/app/components/address-autocomplete-field/address-autocomplete-field.component';
import { environment } from 'src/environments/environment';
import { revealInvalidFields } from 'src/app/core/reveal-invalid-fields';
import {
  negotiatedOfferValueChf,
  trackPurchaseConversion,
} from 'src/app/core/ads-conversions';

@Component({
  selector: 'app-upgrade-ordering-unit',
  standalone: true,
  imports: [
    OfferPointsComponent,
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    FormsModule,
    AddressAutocompleteFieldComponent,
  ],
  templateUrl: './upgrade-ordering-unit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
      input::placeholder {
        color: #717182;
      }
      select {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
      }
      /*
       * Aktionscode row: «Einlösen» / «Entfernen» button matches the Material
       * form-field's filled input area height (~56 px) for clean alignment.
       */
      .bn-promo-action.mat-mdc-button-base {
        height: 56px;
      }
    `,
  ],
})
export class UpgradeOrderingUnitComponent implements OnInit {
  private readonly licenseApi = inject(LicenseApiService);
  private readonly profileService = inject(ProfileService);
  private readonly locationsService = inject(LocationsService);
  private readonly host = inject(ElementRef);

  /** Fires with the same billing payload sent to `POST /v1/licenses/orders` so the parent can create the school. */
  /**
   * The existing school this upgrade pays for. Without it the order is booked
   * to the buyer personally while the address write-back creates a billing
   * account for the school -- and the Abonnement page, which reads the
   * school's account, then finds no subscription.
   */
  @Input() billingOrganisation: BillingOrganisation | null = null;
  @Output() orderCompleted = new EventEmitter<UserBillDetail>();
  @Output() goBack = new EventEmitter<void>();
  /** Wird `true`, sobald der eingelöste Promo-Code die ersten N Monate gratis macht. */
  @Output() firstMonthsFreeChange = new EventEmitter<boolean>();

  readonly baseUrl = environment.homepageBaseUrl;

  location?: GeoLocation;

  // Form State using Signals
  readonly organizationName = model('');
  readonly firstName = model('');
  readonly lastName = model('');
  readonly email = model('');
  readonly phone = model('');
  readonly street = model('');
  readonly zipcode = model('');
  readonly city = model('');
  readonly taxId = model('');

  readonly loading = signal(false);
  readonly submitting = signal(false);
  readonly zipcodeNotFound = signal(false);
  readonly error = signal<string | null>(null);
  readonly accepted = signal(false);

  /**
   * Stable idempotency key for this dialog's order. Reused across retries of the same
   * submission so a re-click after an error replays the order instead of creating
   * (and charging) a second one; a fresh dialog gets a fresh key.
   */
  private readonly orderIdempotencyKey = crypto.randomUUID();
  /** null = noch nicht geprüft, true = Bestellung möglich, false = Lizenz fehlt im Katalog. */
  readonly catalogReady = signal<boolean | null>(null);

  // Plan selection from session
  readonly planSelection = signal<MunicipalityPlanSelection | null>(null);

  /**
   * Negotiated flat-price deal accepted on the offer step. It replaces the whole
   * catalogue calculation: fixed yearly price, no add-ons, no promo code.
   */
  readonly offer = signal<NegotiatedOffer | null>(null);
  readonly formatRappenChf = formatRappenChf;

  // Fields from selection
  readonly selectedDuration = signal<1 | 2 | 3>(1);
  readonly extraAdmins = signal<number>(0);

  // Price calculations
  readonly monthlyRate = computed(() => 49); // Schuleinheit base rate
  /**
   * Basispreis = Monatsrate × Laufzeit (ohne Add-ons, ohne Laufzeitrabatt).
   * Rabatt wird separat als «Laufzeitrabatt»-Zeile ausgewiesen.
   */
  readonly basePriceCalculated = computed(
    () => this.monthlyRate() * 12 * this.selectedDuration()
  );

  /**
   * Label für die «Basispreis»-Zeile (z. B. «2 Jahre» statt «22 Monate»).
   */
  readonly basePricePeriodLabel = computed(() => {
    const years = this.selectedDuration();
    return years === 1 ? '12 Monate' : `${years} Jahre`;
  });

  readonly adminsTotal = computed(
    () => this.extraAdmins() * 9.9 * 12 * this.selectedDuration()
  );

  readonly subtotal = computed(() => {
    return (
      this.monthlyRate() * 12 * this.selectedDuration() + this.adminsTotal()
    );
  });

  readonly discountPercent = computed(() =>
    this.selectedDuration() === 1 ? 0 : this.selectedDuration() === 2 ? 5 : 10
  );
  readonly discountAmount = computed(() => {
    const baseAnnualSubtotal =
      this.monthlyRate() * 12 * this.selectedDuration();
    return (baseAnnualSubtotal * this.discountPercent()) / 100;
  });

  private readonly totalBeforePromo = computed(
    () => this.subtotal() - this.discountAmount()
  );

  readonly monthlyNetRate = computed(() => {
    const months = this.selectedDuration() * 12;
    return months > 0 ? this.totalBeforePromo() / months : 0;
  });

  readonly promoDiscountAmount = computed(() =>
    calcPromoDiscount(
      this.promoInfo(),
      this.totalBeforePromo(),
      this.monthlyNetRate(),
      this.selectedDuration() * 12
    )
  );
  readonly promoDiscountLabelText = computed(() =>
    promoDiscountLabel(this.promoInfo())
  );

  readonly totalAmount = computed(() =>
    Math.max(0, this.totalBeforePromo() - this.promoDiscountAmount())
  );

  /**
   * «Erste N Monate kostenlos»-Aktionscode: blendet eine Hinweisbox
   * «0.00 CHF bis Datum X, danach regulärer Preis» ein.
   */
  readonly isFirstMonthsFreePromo = computed(() => {
    const promo = this.promoInfo();
    return (
      !!promo &&
      promo.duration === 'repeating' &&
      promo.percent_off === 100 &&
      (promo.duration_in_months ?? 0) > 0
    );
  });

  readonly freeUntilDateLabel = computed(() => {
    const promo = this.promoInfo();
    const months = promo?.duration_in_months ?? 0;
    if (!this.isFirstMonthsFreePromo() || months <= 0) return '';
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    return d.toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  });

  // Promotion code validation
  readonly promotionCode = model('');
  readonly validatingPromo = signal(false);
  readonly promoInfo = signal<PromotionCodeInfo | null>(null);
  readonly promoError = signal<string | null>(null);

  async ngOnInit() {
    const storedOffer = loadNegotiatedOfferSelection();
    if (storedOffer?.license_type === 'SCHOOL_UNIT_SUBSCRIPTION') {
      this.offer.set(storedOffer);
      this.clearPromoCode();
    }

    const selection = loadMunicipalityPlanSelection();
    this.planSelection.set(selection);

    if (selection) {
      this.selectedDuration.set(selection.durationYears as 1 | 2 | 3);
      this.extraAdmins.set(selection.extraAdmins);
    }

    this.loading.set(true);
    try {
      const profile = await firstValueFrom(this.profileService.getMe(true));
      const parts = (profile.name || '').trim().split(' ');

      this.firstName.set(parts[0] || '');
      this.lastName.set(parts.slice(1).join(' ') || '');
      this.email.set(profile.email || '');
      this.phone.set((profile as any).phone_number || '');
      this.street.set((profile as any).street || '');
      this.zipcode.set((profile as any).zipcode || '');
      this.city.set((profile as any).city || '');
    } catch (e) {
      console.warn('Failed to prefill billing details:', e);
    } finally {
      this.loading.set(false);
    }

    if (this.offer()) {
      this.catalogReady.set(true);
      return;
    }
    void this.runCatalogPreflight();
  }

  readonly addressComplete = signal(false);
  /** Shown when the order is submitted without accepting the AGB. */
  readonly acceptedMissing = signal(false);
  // The billing fields are template-driven `[(ngModel)]`s with no FormGroup, so
  // we collect their NgModel directives to mark them touched on a failed submit.
  private readonly fieldModels = viewChildren(NgModel);
  private readonly addressField = viewChild(AddressAutocompleteFieldComponent);

  onLocationResolved(location: GeoLocation) {
    this.location = { ...this.location, ...location };
    this.zipcode.set(location.zipcode || this.zipcode());
    this.city.set(location.city || this.city());
    if (location.street) {
      this.street.set(location.street);
    }
    this.zipcodeNotFound.set(false);
  }

  async onValidatePromo() {
    const code = this.promotionCode().trim();
    if (!code) return;

    this.validatingPromo.set(true);
    this.promoError.set(null);
    this.promoInfo.set(null);

    try {
      const res = await firstValueFrom(
        this.licenseApi.validatePromotionCode(code)
      );
      this.promoInfo.set(res);
    } catch (err) {
      this.promoError.set('Ungültiger Aktionscode');
    } finally {
      this.validatingPromo.set(false);
    }
  }

  clearPromoCode() {
    this.promoInfo.set(null);
    this.promoError.set(null);
    this.promotionCode.set('');
  }

  limitLabel(value: number | null): string {
    return value === null ? 'unbegrenzt' : String(value);
  }

  isValid(): boolean {
    return !!(
      this.catalogReady() === true &&
      this.organizationName() &&
      this.firstName() &&
      this.lastName() &&
      this.email() &&
      this.street() &&
      this.zipcode() &&
      this.city() &&
      this.addressComplete()
    );
  }

  /**
   * Verhindert «Submit ins Leere»: Stellt sicher, dass die Lizenz, die
   * `buildSchoolUnitPlanItems` benötigt, vor dem Klick im Katalog vorhanden ist.
   * Spiegelt die Bedingungen aus license-api.service.ts:632-665.
   */
  private async runCatalogPreflight(): Promise<void> {
    this.catalogReady.set(null);
    try {
      const [licenses, orders] = await Promise.all([
        firstValueFrom(
          this.licenseApi.getAvailableLicenses(this.billingOrganisation)
        ),
        firstValueFrom(this.licenseApi.getOrders(this.billingOrganisation)),
      ]);
      const alreadyOwns = orders.some(o =>
        (o.items ?? []).some(
          it =>
            !it.is_cancelled &&
            (it.license?.license_type ?? (it as any).license_type) ===
              'SCHOOL_UNIT_SUBSCRIPTION'
        )
      );
      const hasBase = licenses.some(
        l => l.license_type === 'SCHOOL_UNIT_SUBSCRIPTION'
      );
      const hasUnitAddition = licenses.some(
        l => l.license_type === 'SCHOOL_UNIT_ADDITION_SUBSCRIPTION'
      );
      const hasActiveAuthority = orders.some(o =>
        (o.items ?? []).some(
          it =>
            !it.is_cancelled &&
            (it.license?.license_type ?? (it as any).license_type) ===
              'SCHOOL_AUTHORITY_SUBSCRIPTION'
        )
      );
      const ready =
        alreadyOwns || hasBase || (hasActiveAuthority && hasUnitAddition);
      this.catalogReady.set(ready);
      if (!ready) {
        this.error.set(
          'Die Schuleinheit-Lizenz ist aktuell nicht im Katalog verfügbar. Bitte später erneut versuchen oder den Support kontaktieren.'
        );
      }
    } catch (e) {
      console.warn('Catalog preflight failed:', e);
      this.catalogReady.set(false);
      this.error.set(
        'Lizenzkatalog konnte nicht geladen werden. Bitte später erneut versuchen.'
      );
    }
  }

  formatPrice = formatChf;

  async onSubmit() {
    this.acceptedMissing.set(!this.accepted());
    if (
      !revealInvalidFields(
        [...this.fieldModels(), this.addressField()],
        this.host
      ) ||
      !this.isValid() ||
      !this.accepted() ||
      this.submitting()
    ) {
      return;
    }
    if (this.catalogReady() !== true) return;

    this.submitting.set(true);
    this.error.set(null);

    try {
      const billDetail: UserBillDetail = {
        organization_name: this.organizationName(),
        first_name: this.firstName(),
        last_name: this.lastName(),
        email: this.email(),
        phone: this.phone() || undefined,
        street: this.street(),
        city: this.city(),
        zipcode: this.zipcode(),
        tax_id: this.taxId() || undefined,
      };

      const offer = this.offer();
      if (offer) {
        await firstValueFrom(
          this.licenseApi.acceptNegotiatedOffer(offer.id, billDetail)
        );
        trackPurchaseConversion({
          valueChf: negotiatedOfferValueChf(offer),
          transactionId: offer.id,
          email: this.email(),
          flow: 'angebot',
          plan: 'schuleinheit',
          freeStart: false,
        });
        clearNegotiatedOfferSelection();
        this.firstMonthsFreeChange.emit(false);
        this.orderCompleted.emit(billDetail);
        return;
      }

      await firstValueFrom(
        this.licenseApi.createSchoolUnitPlanOrdersFromSession(
          billDetail,
          this.promoInfo()?.code ?? null,
          this.orderIdempotencyKey,
          this.billingOrganisation
        )
      );
      trackPurchaseConversion({
        valueChf: this.totalAmount(),
        transactionId: this.orderIdempotencyKey,
        email: this.email(),
        flow: 'upgrade',
        plan: 'schuleinheit',
        freeStart: this.isFirstMonthsFreePromo(),
      });
      this.firstMonthsFreeChange.emit(this.isFirstMonthsFreePromo());
      this.orderCompleted.emit(billDetail);
    } catch (e: any) {
      console.error('Order creation failed:', e);
      this.error.set(
        e?.error?.message ||
          e?.message ||
          'Bestellung konnte nicht erstellt werden.'
      );
    } finally {
      this.submitting.set(false);
    }
  }

  toggleAccepted() {
    this.accepted.set(!this.accepted());
  }
}
